import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Trophy,
  Users,
  Target,
  Zap,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface VariantStats {
  variant_id: string;
  variant_name: string;
  is_control: boolean;
  assignments_count: number;
  conversions_count: number;
  conversion_rate: number;
  traffic_allocation: number;
}

interface ABTestResultsProps {
  experimentId: string;
}

export function ABTestResults({ experimentId }: ABTestResultsProps) {
  const [stats, setStats] = useState<VariantStats[]>([]);
  const [experiment, setExperiment] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [checkingWinner, setCheckingWinner] = useState(false);
  const [winnerInfo, setWinnerInfo] = useState<any>(null);

  useEffect(() => {
    loadResults();
  }, [experimentId]);

  const loadResults = async () => {
    try {
      // Load experiment details
      const { data: expData, error: expError } = await supabase
        .from('ab_experiments')
        .select('*')
        .eq('id', experimentId)
        .single();

      if (expError) throw expError;
      setExperiment(expData);

      // Load stats
      const { data: statsData, error: statsError } = await supabase.rpc('get_ab_experiment_stats', {
        p_experiment_id: experimentId,
      });

      if (statsError) throw statsError;
      setStats(statsData || []);
    } catch (error) {
      console.error('Error loading A/B test results:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">Loading results...</p>
        </CardContent>
      </Card>
    );
  }

  if (!experiment) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">Experiment not found</p>
        </CardContent>
      </Card>
    );
  }

  const controlVariant = stats.find((s) => s.is_control);
  const winningVariant = stats.reduce(
    (prev, current) => (current.conversion_rate > prev.conversion_rate ? current : prev),
    stats[0]
  );

  const calculateLift = (variantRate: number) => {
    if (!controlVariant) return 0;
    if (controlVariant.conversion_rate === 0) return 0;
    return ((variantRate - controlVariant.conversion_rate) / controlVariant.conversion_rate) * 100;
  };

  const getLiftIcon = (lift: number) => {
    if (lift > 5) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (lift < -5) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  const checkForWinner = async () => {
    setCheckingWinner(true);
    try {
      const { data, error } = await supabase.functions.invoke('check-ab-test-winners', {
        body: { experiment_id: experimentId },
      });

      if (error) throw error;

      setWinnerInfo(data);

      if (data.winnerDeclared) {
        toast.success(
          `Winner declared! ${data.winner.variant_name} wins with ${data.winner.lift}% lift`
        );
        loadResults(); // Reload to show updated data
      } else if (data.canDeclareWinner === false) {
        toast.info(data.message);
      } else {
        toast.info('No statistically significant winner yet');
      }
    } catch (error: any) {
      console.error('Error checking winner:', error);
      toast.error('Failed to check for winner');
    } finally {
      setCheckingWinner(false);
    }
  };

  const totalVisitors = stats.reduce((sum, s) => sum + s.assignments_count, 0);
  const totalConversions = stats.reduce((sum, s) => sum + s.conversions_count, 0);

  return (
    <div className="space-y-6">
      {winnerInfo && winnerInfo.winnerDeclared && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
          <Trophy className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800 dark:text-green-200">Winner Declared!</AlertTitle>
          <AlertDescription className="text-green-700 dark:text-green-300">
            <strong>{winnerInfo.winner.variant_name}</strong> achieved {winnerInfo.winner.lift}%
            lift over control with {winnerInfo.statistics.confidence_level} confidence (χ² ={' '}
            {winnerInfo.statistics.chi_squared}, p = {winnerInfo.statistics.p_value}). All traffic
            has been automatically switched to the winning variant.
          </AlertDescription>
        </Alert>
      )}

      {winnerInfo && !winnerInfo.winnerDeclared && winnerInfo.statistics && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Statistical Analysis</AlertTitle>
          <AlertDescription>
            {winnerInfo.message}. Current chi-squared: {winnerInfo.statistics.chi_squared}. Need χ²
            &gt; 3.84 with positive lift for 95% confidence.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{experiment.name}</CardTitle>
              <CardDescription>
                {experiment.description || 'Experiment results and performance metrics'}
              </CardDescription>
            </div>
            {experiment.status === 'active' && (
              <Button onClick={checkForWinner} disabled={checkingWinner} className="gap-2">
                <Zap className="h-4 w-4" />
                {checkingWinner ? 'Checking...' : 'Check for Winner'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Total Visitors
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalVisitors.toLocaleString()}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Total Conversions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalConversions.toLocaleString()}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Trophy className="h-4 w-4" />
                  Winning Variant
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{winningVariant?.variant_name}</div>
                <p className="text-xs text-muted-foreground">
                  {winningVariant?.conversion_rate.toFixed(2)}% conversion
                </p>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4">
        {stats.map((variant) => {
          const lift = calculateLift(variant.conversion_rate);
          const isWinner = variant.variant_id === winningVariant?.variant_id;

          return (
            <Card key={variant.variant_id} className={isWinner ? 'border-primary' : ''}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CardTitle>{variant.variant_name}</CardTitle>
                    {variant.is_control && <Badge variant="outline">Control</Badge>}
                    {isWinner && !variant.is_control && (
                      <Badge className="bg-green-500">
                        <Trophy className="h-3 w-3 mr-1" />
                        Winner
                      </Badge>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">{variant.conversion_rate.toFixed(2)}%</div>
                    <div className="text-sm text-muted-foreground">Conversion Rate</div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground">Visitors</div>
                    <div className="font-medium">{variant.assignments_count.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Conversions</div>
                    <div className="font-medium">{variant.conversions_count.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Traffic Split</div>
                    <div className="font-medium">{variant.traffic_allocation}%</div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-muted-foreground">Traffic Distribution</span>
                    <span>
                      {totalVisitors > 0
                        ? ((variant.assignments_count / totalVisitors) * 100).toFixed(1)
                        : 0}
                      % of total
                    </span>
                  </div>
                  <Progress
                    value={
                      totalVisitors > 0 ? (variant.assignments_count / totalVisitors) * 100 : 0
                    }
                    className="h-2"
                  />
                </div>

                {!variant.is_control && controlVariant && (
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      {getLiftIcon(lift)}
                      <span className="text-sm font-medium">
                        {lift > 0 ? '+' : ''}
                        {lift.toFixed(2)}% vs Control
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {lift > 0 ? 'Improvement' : lift < 0 ? 'Decline' : 'No change'}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {stats.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              No data collected yet. Start the experiment to begin gathering results.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
